import { useCallback, useEffect } from 'react';

import type { IPendingInvoiceResponse, PaymentStatus } from '@/shared/api/billing/types';

import { create } from 'zustand';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { useReliableUserRefresh } from '@/entities/user/currentUser/model/useReliableUserRefresh';

import { API } from '@/shared/api';
import { useInvoiceStatusPolling } from '@/shared/hooks';
import { type AppLocale, useInterfaceLanguage } from '@/shared/i18n';

import { trackPendingPaymentModalShown, trackPendingPaymentPollingFinished } from './pendingPaymentAnalytics';

type PaymentResponse = {
  error_code: null | string;
  invoice_id?: null | string;
  invoice_url: null | string;
  paid: boolean;
  status?: PaymentStatus;
};

type ProcessingSession = {
  invoiceId: string;
  invoiceUrl?: string;
  showInvoiceLink: boolean;
  startedAtMs: number;
  successDescription?: string;
  successDescriptionLocale?: AppLocale;
  onPaid?: () => void;
} | null;

type PendingPaymentSessionStore = {
  clearCloseCallback: () => void;
  clearSession: () => void;
  isCloseCallbackPending: boolean;
  markCloseCallbackPending: () => void;
  runCloseCallback: () => void;
  session: ProcessingSession;
  onClosedAfterProcessing?: () => void;
  setInvoiceUrl: (invoiceUrl: string) => void;
  setSession: (session: Exclude<ProcessingSession, null>, onClosedAfterProcessing?: () => void) => void;
};

type ResolvePaymentResultArgs = {
  response: PaymentResponse;
  showInvoiceLink?: boolean;
  successDescription: string;
  onBeforePending?: () => void;
  onBeforeSuccess?: () => void;
  // Runs once the payment has gone through, right away or after the pending invoice is paid.
  onPaid?: () => void;
};

type StartPendingPaymentArgs = {
  invoiceId: string;
  invoiceUrl?: string;
  showInvoiceLink?: boolean;
  successDescription?: string;
  onPaid?: () => void;
};

type ShowPendingInvoiceIfAnyArgs = {
  shouldOpen?: () => boolean;
};

type UsePendingPaymentSessionArgs = {
  onClosedAfterProcessing?: () => void;
};

const getPendingPaymentDurationMs = (startedAtMs: number) => Math.max(0, Date.now() - startedAtMs);

export const usePendingPaymentSessionStore = create<PendingPaymentSessionStore>((set, get) => ({
  clearCloseCallback: () => set({ isCloseCallbackPending: false, onClosedAfterProcessing: undefined }),
  clearSession: () => set({ session: null }),
  isCloseCallbackPending: false,
  markCloseCallbackPending: () => {
    if (get().onClosedAfterProcessing) {
      set({ isCloseCallbackPending: true });
    }
  },
  runCloseCallback: () => {
    const callback = get().onClosedAfterProcessing;
    set({ isCloseCallbackPending: false, session: null, onClosedAfterProcessing: undefined });
    callback?.();
  },
  session: null,
  setInvoiceUrl: (invoiceUrl) =>
    set((state) => ({
      session: state.session
        ? {
            ...state.session,
            invoiceUrl,
            showInvoiceLink: true,
          }
        : state.session,
    })),
  setSession: (session, onClosedAfterProcessing) =>
    set({
      isCloseCallbackPending: true,
      session,
      onClosedAfterProcessing,
    }),
}));

export const usePendingPaymentSession = ({ onClosedAfterProcessing }: UsePendingPaymentSessionArgs = {}) => {
  const { reliableRefresh } = useReliableUserRefresh();
  const { openError, openProcessing, openSuccess } = useAfterPurchaseModalStore();
  const { locale, t } = useInterfaceLanguage();

  const startPendingPayment = useCallback(
    ({ invoiceId, invoiceUrl, showInvoiceLink, successDescription, onPaid }: StartPendingPaymentArgs) => {
      const startedAtMs = Date.now();
      openProcessing({ invoiceUrl });
      trackPendingPaymentModalShown();
      usePendingPaymentSessionStore.getState().setSession(
        {
          invoiceId,
          invoiceUrl,
          showInvoiceLink: showInvoiceLink ?? !!invoiceUrl,
          startedAtMs,
          successDescription,
          successDescriptionLocale: successDescription === undefined ? undefined : locale,
          onPaid,
        },
        onClosedAfterProcessing,
      );
    },
    [locale, onClosedAfterProcessing, openProcessing],
  );

  const showActivePendingSession = useCallback(() => {
    const activeSession = usePendingPaymentSessionStore.getState().session;
    if (!activeSession) return false;

    const modalState = useAfterPurchaseModalStore.getState();
    if (!modalState.isOpen || !modalState.isPending) {
      openProcessing({
        invoiceUrl: activeSession.invoiceUrl,
      });
      trackPendingPaymentModalShown();
      usePendingPaymentSessionStore.getState().markCloseCallbackPending();
    }

    return true;
  }, [openProcessing]);

  const showPendingInvoice = useCallback(
    (pending: IPendingInvoiceResponse) => {
      if (showActivePendingSession()) return;
      if (useAfterPurchaseModalStore.getState().isPending) return;

      startPendingPayment({
        invoiceId: pending.invoice_id,
        invoiceUrl: pending.invoice_url ?? undefined,
        showInvoiceLink: !!pending.invoice_url,
      });
    },
    [showActivePendingSession, startPendingPayment],
  );

  const showPendingInvoiceIfAny = useCallback(
    async ({ shouldOpen }: ShowPendingInvoiceIfAnyArgs = {}) => {
      if (showActivePendingSession()) return true;
      if (useAfterPurchaseModalStore.getState().isPending) return true;

      try {
        const pending = await API.getPendingInvoice();
        if (pending === null) return false;
        if (shouldOpen?.() === false) return false;
        showPendingInvoice(pending);
        return true;
      } catch {
        return false;
      }
    },
    [showActivePendingSession, showPendingInvoice],
  );

  const resolvePaymentResult = useCallback(
    async ({
      response,
      showInvoiceLink,
      successDescription,
      onBeforePending,
      onBeforeSuccess,
      onPaid,
    }: ResolvePaymentResultArgs) => {
      const status = response.status ?? (response.paid ? 'paid' : 'failed');

      if (status === 'pending') {
        if (!response.invoice_id) {
          const errorContent = getPaymentErrorContent(response.error_code, t);
          openError(errorContent.message, response.invoice_url ?? undefined, errorContent.buttonText);
          return;
        }

        onBeforePending?.();
        startPendingPayment({
          invoiceId: response.invoice_id,
          invoiceUrl: response.invoice_url ?? undefined,
          showInvoiceLink: showInvoiceLink ?? !!response.invoice_url,
          successDescription,
          onPaid,
        });
        return;
      }

      if (status === 'failed') {
        const errorContent = getPaymentErrorContent(response.error_code, t);
        openError(errorContent.message, response.invoice_url ?? undefined, errorContent.buttonText);
        return;
      }

      onBeforeSuccess?.();
      onPaid?.();
      await reliableRefresh();
      openSuccess(successDescription, showInvoiceLink ?? false);
    },
    [openError, openSuccess, reliableRefresh, startPendingPayment, t],
  );

  return {
    resolvePaymentResult,
    showPendingInvoiceIfAny,
  };
};

const usePendingPaymentBootstrap = () => {
  const { showPendingInvoiceIfAny } = usePendingPaymentSession();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await showPendingInvoiceIfAny({ shouldOpen: () => !cancelled });
    })();
    return () => {
      cancelled = true;
    };
  }, [showPendingInvoiceIfAny]);
};

const usePendingPaymentSessionController = () => {
  const { reliableRefresh } = useReliableUserRefresh();
  const { isOpen: isAfterPurchaseOpen, openError, openSuccess, setInvoiceUrl } = useAfterPurchaseModalStore();
  const { locale, t } = useInterfaceLanguage();

  const session = usePendingPaymentSessionStore((state) => state.session);
  const isCloseCallbackPending = usePendingPaymentSessionStore((state) => state.isCloseCallbackPending);
  const clearCloseCallback = usePendingPaymentSessionStore((state) => state.clearCloseCallback);
  const clearSession = usePendingPaymentSessionStore((state) => state.clearSession);
  const runCloseCallback = usePendingPaymentSessionStore((state) => state.runCloseCallback);

  useInvoiceStatusPolling({
    enabled: session !== null,
    invoiceId: session?.invoiceId,
    onError: (polledInvoiceUrl) => {
      const errorContent = getPaymentErrorContent(undefined, t);
      const invoiceUrl = polledInvoiceUrl ?? usePendingPaymentSessionStore.getState().session?.invoiceUrl;
      clearSession();
      openError(errorContent.message, invoiceUrl, errorContent.buttonText);
    },
    onFailed: (polledInvoiceUrl) => {
      const errorContent = getPaymentErrorContent(undefined, t);
      const currentSession = usePendingPaymentSessionStore.getState().session;
      const invoiceUrl = polledInvoiceUrl ?? currentSession?.invoiceUrl;
      if (currentSession) {
        trackPendingPaymentPollingFinished({
          durationMs: getPendingPaymentDurationMs(currentSession.startedAtMs),
          status: 'failed',
        });
      }
      clearSession();
      openError(errorContent.message, invoiceUrl, errorContent.buttonText);
    },
    onInvoiceUrl: (invoiceUrl) => {
      usePendingPaymentSessionStore.getState().setInvoiceUrl(invoiceUrl);
      setInvoiceUrl(invoiceUrl);
    },
    onPaid: async () => {
      const currentSession = usePendingPaymentSessionStore.getState().session;
      const description =
        currentSession?.successDescriptionLocale === locale ? currentSession.successDescription : undefined;
      const showInvoiceLink = currentSession?.showInvoiceLink ?? false;
      if (currentSession) {
        trackPendingPaymentPollingFinished({
          durationMs: getPendingPaymentDurationMs(currentSession.startedAtMs),
          status: 'paid',
        });
      }
      currentSession?.onPaid?.();
      await reliableRefresh();
      clearSession();
      openSuccess(description ?? t('billing.result.success'), showInvoiceLink);
    },
  });

  useEffect(() => {
    if (!isAfterPurchaseOpen && isCloseCallbackPending) {
      runCloseCallback();
    }
  }, [isAfterPurchaseOpen, isCloseCallbackPending, runCloseCallback]);

  useEffect(() => {
    if (session !== null || isCloseCallbackPending) return;

    clearCloseCallback();
  }, [clearCloseCallback, isCloseCallbackPending, session]);
};

export const usePendingPaymentSessionLifecycle = () => {
  usePendingPaymentBootstrap();
  usePendingPaymentSessionController();
};
